// =============================================================================
// OFFER DATE RULES (shared by the Create Offer modal and the stage editors)
// =============================================================================
// A new offer must never carry a date that has already passed, and the retention
// bonus installments are a strictly ascending schedule. The stage editors in
// pages/Editpermission reuse these helpers so Create and Edit enforce the same
// rules from one place.
//
// All values are <input type="date"> strings ("YYYY-MM-DD"), which compare
// correctly with plain string comparison — no Date parsing, so there is no
// timezone shifting a day backwards.
// =============================================================================

// Local calendar day as YYYY-MM-DD. Uses local getters (not toISOString) so the
// day never slips across the UTC boundary for users east/west of Greenwich.
export function todayISODate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const clean = (value) => String(value || "").trim();

const isFilled = (value) => /^\d{4}-\d{2}-\d{2}$/.test(clean(value));

// Retention installments in schedule order: [label, date field key].
const INSTALLMENTS = [
  ["Installment 1", "retention_installment1_date"],
  ["Installment 2", "retention_installment2_date"],
  ["Installment 3", "retention_installment3_date"],
];

// Earliest selectable day for an installment input: today, or the previous
// installment's date when that is later. Feeds the native date picker's `min`
// so out-of-order days are not clickable in the first place.
export function retentionInstallmentMin(index, form) {
  const today = todayISODate();

  if (index <= 0) return today;

  const prev = clean(form?.[INSTALLMENTS[index - 1][1]]);
  return isFilled(prev) && prev > today ? prev : today;
}

// Validates the dates on an offer form.
//   requireFutureDates - false for the Edit stage screens, where an offer that is
//                        already past its joining date must stay saveable.
// Returns an array of human-readable messages; empty means valid.
export function getOfferDateErrors(form, { requireFutureDates = true } = {}) {
  const errors = [];
  const today = todayISODate();
  const doj = clean(form?.expected_doj);

  if (requireFutureDates && isFilled(doj) && doj < today) {
    errors.push("Expected DOJ cannot be a past date");
  }

  if (clean(form?.retention_bonus) !== "Yes") return errors;

  let prevDate = "";
  let prevLabel = "";

  INSTALLMENTS.forEach(([label, key], index) => {
    const value = clean(form?.[key]);
    if (!isFilled(value)) return;

    if (requireFutureDates && value < today) {
      errors.push(`Retention ${label} date cannot be a past date`);
    }

    if (prevDate && value <= prevDate) {
      errors.push(
        `Retention ${label} date must be after Retention ${prevLabel} date`
      );
    }

    prevDate = value;
    prevLabel = label;
  });

  return errors;
}